'use client';

import { ActiveAudioItemContext } from 'components';
import AudioDuration from 'components/components/elements/AudioDuration';
import Waveform from 'components/components/elements/Waveform';
import type { ContentEncoding, SearchResult } from 'conversational-search-client';
import { telemetryGaugeAction } from 'core-events/actions';
import React, { type JSX, useCallback, useContext, useMemo, useState } from 'react';
import type { AudioStockItem } from 'search-gateway-client';
import { twMerge } from 'tailwind-merge';

import { DownloadBottom, Pause, Play } from '@videoblocks/react-icons';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { downloadStockItemAction, downloadStockItemPreviewAction } from '../../_actions/downloadActions';
import { DEFAULT_DOWNLOAD_ERROR_MESSAGE, useChatSessionContext } from '../../_context/SessionContext';
import { formatFileSize } from '../../_utils';
import { type AudioFormat, AudioDownloadPopover } from './AudioDownloadPopover';

const logger = LoggerFactory.create('convSearchAudioResultCard');

interface AudioResultCardProps {
    result: SearchResult;
}

const MAX_TAGS = 3;

function buildAudioFormats(encodings: ContentEncoding[]): AudioFormat[] {
    const seen = new Set<string>();
    const audioFormats: AudioFormat[] = [];
    for (const enc of encodings) {
        if (!enc.format) continue;
        const formatName = enc.format.toUpperCase();
        if (seen.has(formatName)) continue;
        seen.add(formatName);
        audioFormats.push({
            formatName,
            label: formatName,
            fileSize: formatFileSize(enc.fileSizeBytes),
        });
    }
    return audioFormats;
}

export function AudioResultCard({ result }: AudioResultCardProps): JSX.Element {
    const { activeAudioItemState, setActiveAudioItemState } = useContext(ActiveAudioItemContext);
    const { canDownloadAudio, setDownloadError } = useChatSessionContext();

    // The `components/` audio primitives (Waveform, ActiveAudioItemContext) are
    // typed to `AudioStockItem` from the legacy search-gateway shape, but only
    // read the fields below. Per SearchResultEnrichmentService, audio items
    // carry the MP3 URL in `thumbnailUrl` and the waveform JSON URL in
    // `previewUrl`. Cast through `unknown` to avoid padding the unused fields.
    const stockItem = useMemo(
        () =>
            ({
                id: result.contentId,
                title: result.title,
                previewUrl: result.thumbnailUrl,
                waveformUrl: result.previewUrl,
                duration: result.duration ?? 0,
                stockblocksId: result.stockblocksId ?? result.contentId,
            }) as unknown as AudioStockItem,
        [result],
    );

    const isActive = activeAudioItemState.stockItem?.id === stockItem.id;
    const isPlaying = isActive && activeAudioItemState.isPlaying;

    const [showPopover, setShowPopover] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const formats = useMemo<AudioFormat[]>(
        () => buildAudioFormats(result.downloadEncodings ?? []),
        [result.downloadEncodings],
    );

    const handlePlayToggle = useCallback((): void => {
        setActiveAudioItemState({ stockItem, isPlaying: !isPlaying });
    }, [isPlaying, setActiveAudioItemState, stockItem]);

    const handleWaveformActivate = useCallback(
        (nextIsPlaying: boolean): void => {
            setActiveAudioItemState({ stockItem, isPlaying: nextIsPlaying });
        },
        [setActiveAudioItemState, stockItem],
    );

    const handleDownloadClick = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        setShowPopover(true);
    }, []);

    const handlePreviewDownloadClick = useCallback(
        async (e: React.MouseEvent): Promise<void> => {
            e.preventDefault();
            e.stopPropagation();
            if (isDownloading) return;

            setIsDownloading(true);
            try {
                const downloadResult = await downloadStockItemPreviewAction(
                    stockItem.stockblocksId,
                    result.contentClass,
                );
                if (downloadResult.ok) {
                    void telemetryGaugeAction('conv-search.preview.downloaded', result.contentId);
                    window.location.href = downloadResult.url;
                } else {
                    setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
                }
            } catch (error: unknown) {
                logger.warn(
                    { error, stockblocksId: stockItem.stockblocksId, contentClass: result.contentClass },
                    'Preview download action threw on client',
                );
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            } finally {
                setIsDownloading(false);
            }
        },
        [isDownloading, stockItem.stockblocksId, result.contentClass, result.contentId, setDownloadError],
    );

    const handleFormatSelect = useCallback(
        async (formatName: string): Promise<void> => {
            setIsDownloading(true);
            try {
                const downloadResult = await downloadStockItemAction(
                    stockItem.stockblocksId,
                    formatName,
                    result.contentClass,
                );
                if (downloadResult.ok) {
                    void telemetryGaugeAction('conv-search.item.download', result.contentId);
                    window.location.href = downloadResult.url;
                } else {
                    setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
                }
            } catch (error: unknown) {
                logger.warn(
                    { error, stockblocksId: stockItem.stockblocksId, formatName, contentClass: result.contentClass },
                    'Download action threw on client',
                );
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            } finally {
                setIsDownloading(false);
                setShowPopover(false);
            }
        },
        [stockItem.stockblocksId, result.contentClass, result.contentId, setDownloadError],
    );

    const closePopover = useCallback((): void => {
        setShowPopover(false);
    }, []);

    return (
        <div
            className={twMerge(
                'group relative flex flex-row items-center gap-8 rounded-lg border-2 border-solid px-4 pt-4 pb-6 transition-colors',
                isPlaying || isActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-blue-500 hover:bg-blue-50',
            )}
        >
            <div className="flex w-full flex-col gap-4">
                <div className="flex flex-row items-center gap-3">
                    <button
                        type="button"
                        onClick={handlePlayToggle}
                        aria-label={isPlaying ? 'pause' : 'play'}
                        title={isPlaying ? 'pause' : 'play'}
                        className={twMerge(
                            'flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                            isPlaying
                                ? 'bg-blue-500 text-white'
                                : 'bg-yellow-400 text-gray-900 group-hover:bg-blue-800 group-hover:text-white',
                        )}
                    >
                        {isPlaying ? (
                            <Pause className="h-4 w-4 fill-current" />
                        ) : (
                            <Play className="h-4 w-4 fill-current" />
                        )}
                    </button>

                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <a
                            href={result.detailsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                void telemetryGaugeAction('conv-search.item.clickthrough', result.contentId);
                            }}
                            className={twMerge(
                                'truncate text-base font-semibold leading-[22px] text-gray-900 no-underline',
                                'group-hover:underline',
                            )}
                        >
                            {result.title}
                        </a>
                        {result.artistNames?.[0] && (
                            <span className="truncate text-xs font-semibold leading-[18px] text-gray-600">
                                {result.artistNames[0]}
                            </span>
                        )}
                        {(() => {
                            const tags = result.topTags?.slice(0, MAX_TAGS) ?? [];
                            if (tags.length === 0) return null;
                            return (
                                <div className="flex flex-row flex-wrap items-start gap-1 text-xs leading-[18px]">
                                    {tags.map((tag, i) => (
                                        <React.Fragment key={`${tag}-${i}`}>
                                            <span className="font-semibold text-gray-700">{tag}</span>
                                            {i < tags.length - 1 && (
                                                <span className="font-normal text-gray-900" aria-hidden="true">
                                                    •
                                                </span>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>

                    <button
                        type="button"
                        onClick={
                            canDownloadAudio
                                ? handleDownloadClick
                                : (e: React.MouseEvent): void => {
                                      void handlePreviewDownloadClick(e);
                                  }
                        }
                        disabled={isDownloading}
                        aria-label={canDownloadAudio ? 'Download audio' : 'Download preview'}
                        title={canDownloadAudio ? 'Download audio' : 'Download preview'}
                        className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <DownloadBottom className="h-4 w-4" fill="currentColor" />
                    </button>
                </div>

                <div className="flex flex-row items-center gap-6">
                    <Waveform
                        stockItem={stockItem}
                        isPlaying={isPlaying}
                        handleAudioItemClick={handleWaveformActivate}
                        className="h-4 max-h-4 flex-1"
                    />
                    <AudioDuration
                        duration={stockItem.duration}
                        className="order-1 lg:order-1 text-xs font-semibold leading-[18px] text-gray-600"
                    />
                </div>
            </div>

            {canDownloadAudio && showPopover && (
                <AudioDownloadPopover
                    formats={formats}
                    isDownloading={isDownloading}
                    onSelectFormat={handleFormatSelect}
                    onClose={closePopover}
                />
            )}
        </div>
    );
}
